/**
 * ───────────────────────────────────────────────────────────────────────────
 * AUDIENCE MODEL — SINGLE SOURCE OF TRUTH
 * ───────────────────────────────────────────────────────────────────────────
 *
 * GIGGONE serves FOUR audiences, not one. Before this file the home page,
 * the register screen and the footer each implied a different, narrower
 * picture (mostly homeowner -> sub). Everything that renders an audience —
 * the hero selector, the role-path cards, the header nav, the footer — now
 * reads this list, so adding or renaming an audience is a one-line edit and
 * can never drift between components.
 *
 * ACCOUNT TYPES ARE UNCHANGED. There are still exactly three account roles in
 * the database (`homeowner`, `gc`, `subcontractor`). "Commercial" is not a
 * fourth account type — it is a commercial PROJECT posted by a project owner
 * account, which is why its signup link carries `type=commercial` and the
 * listing itself is stored with project_type='commercial' (the value the
 * marketplace already filters on). Nothing about auth or RLS changes here.
 */

import { Home, Building2, Hammer, HardHat } from 'lucide-react';

export type AudienceKey = 'homeowner' | 'commercial' | 'gc' | 'subcontractor';

export interface Audience {
  key: AudienceKey;
  /** lucide icon component rendered by the consuming view. */
  icon: typeof Home;
  /** i18n keys — copy lives in translations.ts, never inline. */
  titleKey: string;
  ctaKey: string;
  descKey: string;
  /**
   * SHORT plural label used in the header navigation ("Homeowners",
   * "Contractors"). The card titles are singular ("Homeowner") because a card
   * addresses one visitor; a nav item names a segment. Both live here so the
   * nav and the hero selector can never drift apart.
   */
  navKey: string;
  /** Primary conversion destination, relative (the caller localises it). */
  to: string;
  /** Secondary "learn more" landing page for this audience. */
  learn: string;
  /** Tailwind classes for the icon chip. */
  accent: string;
  /** Ring/border treatment when the card is emphasised. */
  featured?: boolean;
}


/**
 * Order is deliberate and mirrors the funnel the business wants to grow:
 * demand first (who posts the work), supply second (who buys it).
 */
export const AUDIENCES: Audience[] = [
  {
    key: 'homeowner',
    icon: Home,
    titleKey: 'aud.homeownerTitle',
    ctaKey: 'aud.homeownerCta',
    descKey: 'aud.homeownerDesc',
    navKey: 'nav.homeowners',
    to: '/register?role=homeowner',
    learn: '/homeowners',
    accent: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  },
  {
    key: 'commercial',
    icon: Building2,
    titleKey: 'aud.commercialTitle',
    ctaKey: 'aud.commercialCta',
    descKey: 'aud.commercialDesc',
    navKey: 'nav.commercial',
    to: '/register?role=homeowner&type=commercial',
    learn: '/commercial',
    accent: 'bg-blue-50 text-brand-blue ring-blue-100',
  },
  {
    key: 'gc',
    icon: Hammer,
    titleKey: 'aud.gcTitle',
    ctaKey: 'aud.gcCta',
    descKey: 'aud.gcDesc',
    navKey: 'nav.contractors',
    to: '/register?role=gc',
    learn: '/general-contractors',
    accent: 'bg-amber-50 text-amber-700 ring-amber-100',
  },
  {
    key: 'subcontractor',
    icon: HardHat,
    titleKey: 'aud.subTitle',
    ctaKey: 'aud.subCta',
    descKey: 'aud.subDesc',
    navKey: 'nav.subcontractors',
    to: '/jobs',
    learn: '/subcontractors',
    accent: 'bg-orange-50 text-brand-orange ring-orange-100',
    featured: true,
  },
];

/**
 * HEADER NAVIGATION SOURCE.
 *
 * The audience nav items point at the dedicated LANDING page (`learn`), not at
 * the signup/conversion URL. A nav item is an orientation control ("who is this
 * site for?"), so it must resolve to a crawlable, indexable, shareable page —
 * `/homeowners`, `/commercial`, `/general-contractors`, `/subcontractors` — all
 * four of which are already in the sitemap with an EN/ES hreflang pair.
 * Sending nav traffic straight to `/register` would have created uncrawlable,
 * parameterised destinations in the primary navigation.
 */
export interface AudienceNavItem {
  key: AudienceKey;
  icon: typeof Home;
  labelKey: string;
  descKey: string;
  to: string;
  accent: string;
}

export const AUDIENCE_NAV: AudienceNavItem[] = AUDIENCES.map((a) => ({
  key: a.key,
  icon: a.icon,
  labelKey: a.navKey,
  descKey: a.descKey,
  to: a.learn,
  accent: a.accent,
}));

/**
 * PRIMARY ROLE ENTRY POINTS — "one click, straight to the point".
 *
 * The three doors rendered ABOVE THE FOLD on the home page hero and at the top
 * of every public landing page (RoleQuickStart). Each card is ONE click into
 * the real workflow, and the destination adapts to the visitor:
 *   - signed out  -> the signup screen pre-set to that role (or the marketplace
 *                    for contractors, who can browse leads before an account)
 *   - signed in   -> straight to the action (post / leads / work)
 * Copy is bilingual here so the hero and the landing pages cannot drift.
 */
export type PrimaryRoleKey = 'homeowner' | 'contractor' | 'subcontractor';

export interface PrimaryRole {
  key: PrimaryRoleKey;
  icon: typeof Home;
  title: { en: string; es: string };
  action: { en: string; es: string };
  hint: { en: string; es: string };
  /** Destination when signed out (relative, caller localises). */
  guestTo: string;
  /** Destination when signed in with a matching or any account. */
  memberTo: string;
  /** Tailwind gradient + chip classes. */
  tone: string;
  chip: string;
}

export const PRIMARY_ROLES: PrimaryRole[] = [
  {
    key: 'homeowner',
    icon: Home,
    title: { en: 'I am a Homeowner', es: 'Soy propietario' },
    action: { en: 'Post a project', es: 'Publicar un proyecto' },
    hint: { en: 'Free to post · get contacted by local pros', es: 'Publicar es gratis · reciba contacto de profesionales' },
    guestTo: '/register?role=homeowner',
    memberTo: '/dashboard',
    tone: 'from-emerald-500 to-emerald-600',
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  },
  {
    key: 'contractor',
    icon: Hammer,
    title: { en: 'I am a Contractor', es: 'Soy contratista' },
    action: { en: 'Find and buy leads', es: 'Encontrar y comprar leads' },
    hint: { en: 'Browse open projects now · no signup to look', es: 'Vea proyectos abiertos · sin registro para mirar' },
    guestTo: '/jobs',
    memberTo: '/jobs',
    tone: 'from-brand-orange to-orange-600',
    chip: 'bg-orange-50 text-brand-orange ring-orange-200',
  },
  {
    key: 'subcontractor',
    icon: HardHat,
    title: { en: 'I am a Subcontractor', es: 'Soy subcontratista' },
    action: { en: 'Join projects and find work', es: 'Unirse a proyectos y encontrar trabajo' },
    hint: { en: 'Create your free profile · get matched to work', es: 'Cree su perfil gratis · reciba trabajo' },
    guestTo: '/register?role=subcontractor',
    memberTo: '/jobs',
    tone: 'from-blue-600 to-blue-700',
    chip: 'bg-blue-50 text-blue-700 ring-blue-200',
  },
];


/**
 * COMMERCIAL PROPERTY SEGMENTS.
 *
 * These are marketing entry points, NOT invented inventory counts. Every chip
 * links into the real marketplace filtered to commercial work
 * (`/jobs?type=commercial`), which is the same `project_type` filter the browse
 * page already supports — so a click always lands on live rows or the honest
 * empty state, never on a fabricated segment page.
 */
export interface CommercialSegment {
  key: string;
  labelKey: string;
  /** Optional trade pre-filter appended to the commercial feed. */
  trade?: string;
}

export const COMMERCIAL_SEGMENTS: CommercialSegment[] = [
  { key: 'property-managers', labelKey: 'aud.segPropertyMgr' },
  { key: 'retail', labelKey: 'aud.segRetail' },
  { key: 'restaurants', labelKey: 'aud.segRestaurants' },
  { key: 'warehouses', labelKey: 'aud.segWarehouse' },
  { key: 'office', labelKey: 'aud.segOffice' },
  { key: 'multifamily', labelKey: 'aud.segMultifamily' },
];

/** Query string for a commercial segment entry point. */
export const commercialFeedQuery = (seg?: CommercialSegment): string =>
  seg?.trade ? `?type=commercial&trade=${seg.trade}` : '?type=commercial';
